import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useCreateBriefPayment } from "@entities/brief/lib/hooks/useCreateBriefPayment";
import { useEditBriefPayment } from "@entities/brief/lib/hooks/useEditBriefPayment";
import { useGetBriefPaymentAggregation } from "@entities/brief/lib/hooks/useGetBriefPaymentAggregation";
import { useGetBriefPaymentFacets } from "@entities/brief/lib/hooks/useGetBriefPaymentFacets";
import { useGetBriefPayments } from "@entities/brief/lib/hooks/useGetBriefPayments";
import {
  IBriefPayment,
  IBriefPaymentSearchRequest,
  ICreateBriefPayment,
  IEditBriefPayment,
  IGetBriefPaymentAggregation,
  PAYMENT_TYPE,
} from "@shared/lib/types/briefPayment";
import { formatData } from "@shared/lib/utils/formatData";
import { Table, TableTitleLayout } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import {
  Card,
  Col,
  DatePicker,
  Divider,
  Flex,
  Input,
  Modal,
  Row,
  Tag,
  Typography
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import { useEffect, useState } from "react";
import { CreateBriefPaymentEntry } from "./CreateBriefPaymentEntry";
import { EditBriefPaymentEntry } from "./EditBriefPaymentEntry";

const { Title } = Typography;
const { RangePicker } = DatePicker;

export function BriefPayment({ briefId }: { briefId: string }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [tableType, setTableType] = useState<PAYMENT_TYPE[]>([]);
  const [billingIds, setBillingIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<IBriefPayment | null>(
    null
  );

  const searchParams: IBriefPaymentSearchRequest = {
    briefId,
    searchQuery,
    type: tableType,
    billingId: billingIds,
    createdAtFrom: dateRange[0]?.startOf("day").toISOString() || "",
    createdAtTo: dateRange[1]?.endOf("day").toISOString() || "",
  };

  const { payments, isFetching, totalCount } =
    useGetBriefPayments(searchParams);
  const { facets } = useGetBriefPaymentFacets();
  const { create, isCreating, isCreated } = useCreateBriefPayment();
  const { edit, isEditing, isEdited } = useEditBriefPayment();

  const { aggregation } = useGetBriefPaymentAggregation(briefId);

  const typeToDisplay = (facets?.type || []).map((item) => item._id);
  const billingIdsToDisplay = (facets?.billing || []).map((item) => ({
    name: item.name,
    value: item._id,
  }));

  useEffect(() => {
    if (isCreated) {
      setCreateModalOpen(false);
    }
  }, [isCreated]);

  useEffect(() => {
    if (isEdited) {
      setEditModalOpen(false);
      setSelectedPayment(null);
    }
  }, [isEdited]);

  const handleCreate = (values: ICreateBriefPayment) => {
    create({
      ...values,
      briefId: briefId,
    });
  };

  const handleEdit = (values: IEditBriefPayment) => {
    edit(values);
  };

  const handleEditClick = (record: IBriefPayment) => {
    setSelectedPayment(record);
    setEditModalOpen(true);
  };

  const columns: ColumnsType<IBriefPayment> = [
    {
      title: "Title",
      key: "title",
      render: (_: string, record: IBriefPayment) => (
        <TextWithTooltip text={record.title} />
      ),
    },
    {
      title: "Created At",
      key: "createdAt",
      filterCount: dateRange[0] || dateRange[1] ? 1 : 0,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!(dateRange[0] || dateRange[1]),
      render: (_: string, record: IBriefPayment) => (
        <div>{dayjs(record.createdAt).format("DD/MM/YYYY")}</div>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            allowEmpty={[true, true]}
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
              props.confirm();
            }}
            style={{ marginBottom: 8, width: "100%" }}
            showTime={false}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              size="small"
              onClick={() => {
                setDateRange([null, null]);
                props.confirm();
              }}
            >
              Reset
            </Button>
            <Button type="primary" size="small" onClick={props.confirm}>
              OK
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Proof URL",
      key: "proofUrl",
      render: (_: string, record: IBriefPayment) => (
        <>
          {record?.proofUrl ? (
            <Button
              type="link"
              href={record.proofUrl}
              onClick={() => window.open(record.proofUrl, "_blank")}
            >
              <TextWithTooltip text={record.proofUrl} />
            </Button>
          ) : (
            "N/A"
          )}
        </>
      ),
    },
    {
      title: "Billing",
      key: "billing",
      filterCount: billingIds.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!billingIds.length,
      render: (_: string, record: IBriefPayment) => (
        <div>{record.billing || "N/A"}</div>
      ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Billing"
          props={props}
          placeholder="Select Billing"
          initialTagCount={3}
          itemsToDisplay={billingIdsToDisplay}
          selectedItems={billingIds}
          handleSelectionChange={(items: string[]) => setBillingIds(items)}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filterCount: tableType.length,
      filterDropdownProps: { destroyPopupOnHide: true },
      filtered: !!tableType.length,
      render: (type: PAYMENT_TYPE) =>
        type === PAYMENT_TYPE.Credit ? (
          <Tag color="red">{type}</Tag>
        ) : (
          <Tag color="green">{type}</Tag>
        ),
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          title="Type"
          props={props}
          placeholder="Select Type"
          initialTagCount={3}
          itemsToDisplay={typeToDisplay}
          selectedItems={tableType}
          handleSelectionChange={(items: string[]) =>
            setTableType(items as PAYMENT_TYPE[])
          }
        />
      ),
    },
    {
      title: "Is TDS Deducted? ",
      key: "isTdsDeducted",
      render: (_: string, record: IBriefPayment) => (
        <div>
          <Tag color={record?.isTdsDeducted ? "green" : "red"}>
            {record?.isTdsDeducted ? "Yes" : "No"}
          </Tag>
        </div>
      ),
    },
    {
      title: "Is TDS Received?",
      key: "tdsReceived",
      render: (_: string, record: IBriefPayment) => (
        <div>
          <Tag color={record?.isTdsReceived ? "green" : "red"}>
            {record?.isTdsReceived ? "Yes" : "No"}
          </Tag>
        </div>
      ),
    },
    {
      title: "TDS Amount",
      key: "tdsAmount",
      render: (_: string, record: IBriefPayment) => (
        <div>
          {record?.tdsAmount ? formatData(record.tdsAmount || 0) : "N/A"}
        </div>
      ),
    },

    {
      title: "Amount Received",
      key: "amountReceived",
      render: (_: string, record: IBriefPayment) => (
        <div>{formatData(record.amountReceived || 0)}</div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: string, record: IBriefPayment) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            type="text"
          />
        </>
      ),
    },
  ];

  return (
    <>
        <Table
          columns={columns}
          title={() => <TableTitleLayout 
            filterComponent={<div style={{width: "100%"}}>
              <Flex justify="space-between" style={{width: "100%"}}>
                <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title or billing ID"
                            style={{width: 300}}

              prefix={<Search size={14}/>}
            />
            <Button
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              type="primary"
            >
              Create Payment
            </Button>
              </Flex>
                            <Divider />
              <Row gutter={16}>
          {aggregation?.all && (
            <Col span={8}>
              <AggregationCard values={aggregation?.all} title="All" />
            </Col>
          )}
          {aggregation?.debits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.debits} title="Debits" />
            </Col>
          )}
          {aggregation?.credits && (
            <Col span={8}>
              <AggregationCard values={aggregation?.credits} title="Credits" />
            </Col>
          )}
        </Row>
            </div>}
            totalText={`${totalCount || 0} Records found`}
          />}
          loading={isFetching}
          dataSource={payments}
          scroll={{ x: 1000 }}
        />

      <Modal
        title="Create Payment"
        open={isCreateModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
        }}
        footer={null}
        destroyOnClose
      >
        <CreateBriefPaymentEntry
          onSubmit={handleCreate}
          isSubmitting={isCreating}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      <Modal
        title="Edit Payment"
        open={isEditModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setSelectedPayment(null);
        }}
        footer={null}
        destroyOnClose
      >
        {selectedPayment && (
          <EditBriefPaymentEntry
            onSubmit={handleEdit}
            isSubmitting={isEditing}
            onCancel={() => {
              setEditModalOpen(false);
              setSelectedPayment(null);
            }}
            initialValues={selectedPayment}
          />
        )}
      </Modal>
    </>
  );
}

export function AggregationCard({
  values,
  title,
}: {
  values: IGetBriefPaymentAggregation["debits"];
  title: string;
}) {
  return (
    <Card title={title} bordered={false}>
      <Flex vertical>
        <div>Amount Received : {formatData(values?.amountReceived || 0)}</div>
        <div>Tds Amount : {formatData(values?.tdsAmount || 0)}</div>
      </Flex>
    </Card>
  );
}
